import { makeStyles, Checkbox, FormControlLabel } from '@material-ui/core';
import { useState } from 'react';
import { CustomQuestion } from '../../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../../api/CustomQuestion/types/Type';
import {
  CustomSelect,
  FormGroup,
  Text,
  TextField,
  TextWysiwyg,
} from '../../components/common';
import AttachFilesButton from '../../components/common/attach-files-button';
import AttachmentCard from '../../components/common/attachment-card';
import { MONTSERRAT_FONT_FAMILY } from '../../theme/variables';
import { customFieldsMaxLength } from '../../utils/custom-questions';

const useStyles = makeStyles({
  label: {
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
  },
  subtitle: {
    fontSize: '24px',
    lineHeight: '36px',
    fontFamily: MONTSERRAT_FONT_FAMILY,
    fontWeight: 700,
  },
  fileUploadGroup: {
    gap: '12px',
  },
  buttonWrapper: {
    width: 'auto',
    marginLeft: '-16px',
  },
  cardWrapper: {
    width: '100%',
    maxWidth: '500px',
  },
});

type CustomQuestionPreviewProps = {
  item: CustomQuestion;
};

export function CustomQuestionPreview({ item }: CustomQuestionPreviewProps) {
  switch (item.type) {
    case CustomQuestionType.TEXT:
      return <TextField label={item.label} />;
    case CustomQuestionType.SINGLE_CHOICE:
      return <SingleChoicePreview item={item} />;
    case CustomQuestionType.MULTIPLE_CHOICE:
      return <MultipleChoicePreview item={item} />;
    case CustomQuestionType.NUMBER:
      return <NumberPreview item={item} />;
    case CustomQuestionType.PARAGRAPH:
      return <TextWysiwygPreview item={item} />;
    case CustomQuestionType.SUBTITLE:
      return <SubtitlePreview item={item} />;
    case CustomQuestionType.ATTACHMENT:
      return <FileUploadPreview item={item} />;
    default:
      return null;
  }
}

const TextWysiwygPreview = ({ item }: CustomQuestionPreviewProps) => {
  const [value, setValue] = useState('');
  return (
    <TextWysiwyg
      label={item.label}
      placeholder={item.label}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      showCount
      maxLength={customFieldsMaxLength[CustomQuestionType.PARAGRAPH]}
    />
  );
};

const NumberPreview = ({ item }: CustomQuestionPreviewProps) => {
  return (
    <TextField
      label={item.label}
      type='number'
      InputProps={{
        inputProps: {
          min: 0,
        },
      }}
      onKeyPress={(event) => {
        const numericRegex = /^[0-9]*$/;
        if (!numericRegex.test(event.key)) {
          event.preventDefault();
        }
      }}
    />
  );
};

const SubtitlePreview = ({ item }: CustomQuestionPreviewProps) => {
  const classes = useStyles();
  return <h3 className={classes.subtitle}>{item.label}</h3>;
};

const SingleChoicePreview = ({ item }: CustomQuestionPreviewProps) => {
  const [value, setValue] = useState('');
  return (
    <CustomSelect
      label={item.label}
      onChange={(e) => setValue(e.target.value)}
      options={
        item.options?.map((option) => ({
          label: option.label,
          value: option.label,
        })) || []
      }
      value={value}
    />
  );
};

const MultipleChoicePreview = ({ item }: CustomQuestionPreviewProps) => {
  const classes = useStyles();
  return (
    <FormGroup column>
      <Text variant='normal' className={classes.label}>
        {item.label}
      </Text>
      {item.options?.map((option, idx) => (
        <FormControlLabel
          control={<Checkbox color='primary' />}
          label={option.label}
          key={`checkbox-${idx}`}
        />
      ))}
    </FormGroup>
  );
};

const FileUploadPreview = ({ item }: CustomQuestionPreviewProps) => {
  const classes = useStyles();

  const placeholderAttachment = {
    name: 'example-file',
    extension: 'pdf',
    size: 102400,
    url: undefined,
  };

  const handleFileSelect = () => { };

  const label = item.label + (item.isMandatory ? '*' : '');

  const preventClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <FormGroup
      column
      className={classes.fileUploadGroup}
      onClick={preventClick}>
      <Text variant='normal' className={classes.label}>
        {label}
      </Text>
      <div className={classes.buttonWrapper}>
        <AttachFilesButton
          onChange={handleFileSelect}
          text='Attach file'
          accept='.pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.txt,.png,.jpeg,.jpg,.gif'
        />
      </div>
      <div
        className={classes.cardWrapper}
        onClickCapture={preventClick}
        onMouseDownCapture={preventClick}>
        <AttachmentCard
          attachment={placeholderAttachment}
          loading={false}
          onRemove={() => { }}
        />
      </div>
    </FormGroup>
  );
};
